import InfiniteScroll from 'react-infinite-scroll-component';
import { Disclosure } from '@headlessui/react';
import { Role } from 'auth';
import isEmpty from 'lodash/isEmpty';
import { FC, Fragment, useCallback, useState } from 'react';
import '@/modules/common/styles/common.css';
import EntitiesFilters from '@/modules/admin-dashboard/components/EntitiesFilters.tsx';
import { AdminEntityApprovedItem } from '@/modules/admin-dashboard/components/items/AdminEntityApprovedItem.tsx';
import { AdminEntityAwaitingApprovalItem } from '@/modules/admin-dashboard/components/items/AdminEntityAwaitingApprovalItem.tsx';
import AdminEntityItem from '@/modules/admin-dashboard/components/items/AdminEntityItem.tsx';
import {
  AdminEntityInfoModal,
  AdminEntityInfoModalProps,
} from '@/modules/admin-dashboard/components/modals/AdminEntityInfoModal.tsx';
import { useAdminEntitiesListQuery } from '@/modules/admin-dashboard/queries/entities.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { EntityWithDocumentFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { EmptyListState, PageLayout, SearchField, Spinner, TabItem } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { EntityStatus, GetAdminEntitiesListV1UsersAdminEntitiesGetParams } from 'api';

type EntityStatusTabs = 'all' | EntityStatus.EntityApproved | EntityStatus.EntityAwaitingApproval;

const tabItems: TabItem<EntityStatusTabs>[] = [
  { name: 'all', label: 'All', container: '' },
  { name: EntityStatus.EntityAwaitingApproval, label: 'Requested', container: '' },
  { name: EntityStatus.EntityApproved, label: 'Approved', container: '' },
];

export interface EntityItemProps extends EntityWithDocumentFullResponse {
  handleOpenEntityInfoModal: (modalProps: Partial<AdminEntityInfoModalProps>) => void;
}

interface UniqEntityStatusData {
  emptyListTitle: string;
  emptyListDescription: string;
  listItem: FC<EntityItemProps>;
}

export type Filters = Pick<
  GetAdminEntitiesListV1UsersAdminEntitiesGetParams,
  'search' | 'type' | 'apply_register_id' | 'register_type'
>;

const mapEntityStatusToUniqData: Record<EntityStatusTabs, UniqEntityStatusData> = {
  all: {
    emptyListTitle: 'No holders yet',
    emptyListDescription: 'Holders will appear here',
    listItem: AdminEntityItem,
  },
  [EntityStatus.EntityAwaitingApproval]: {
    emptyListTitle: 'No pending holders requests',
    emptyListDescription: 'Holders requests which cannot be handled by BronID will appear here',
    listItem: AdminEntityAwaitingApprovalItem,
  },
  [EntityStatus.EntityApproved]: {
    emptyListTitle: 'No approved holders yet',
    emptyListDescription: 'All approved investor holders will appear here',
    listItem: AdminEntityApprovedItem,
  },
};

const AdminEntities = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [activeTab, setActiveTab] = useState<EntityStatusTabs>(EntityStatus.EntityAwaitingApproval);
  const [isEntityInfoModalOpen, setIsEntityInfoModalOpen] = useState(false);
  const [entityInfoModalProps, setEntityInfoModalProps] = useState<
    Partial<AdminEntityInfoModalProps> | undefined
  >();
  const [filters, setFilters] = useState<Filters>({});

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage } =
    useAdminEntitiesListQuery({
      page: 1,
      page_size: 15,
      status: activeTab === 'all' ? undefined : activeTab,
      ...filters,
    });

  const headerAction = (
    <div className="flex flex-1 flex-wrap justify-end gap-3">
      <SearchField
        placeholder="Search"
        className="max-w-[250px]"
        onChange={value => setFilters(prev => ({ ...prev, search: value }))}
      />
      <Disclosure.Button onClick={() => setShowFilter(prev => !prev)}>
        <div className="flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
          <img alt="filters" src={FiltersIcon} />
          <span className="text-sm">Filters</span>
        </div>
      </Disclosure.Button>
    </div>
  );

  const uniqEntityStatusInfo = mapEntityStatusToUniqData[activeTab];

  const ListItem = uniqEntityStatusInfo.listItem;

  const isFiltersUsed = !isEmpty(filters);

  const handleOpenEntityInfoModal = useCallback(
    (modalProps: Partial<AdminEntityInfoModalProps>) => {
      setEntityInfoModalProps(modalProps);
      setIsEntityInfoModalOpen(true);
    },
    [],
  );

  return (
    <PageLayout
      title="Holders"
      tabs={tabItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      headerAction={headerAction}
      HeaderWrapper={Disclosure}
      headerBottomContent={
        showFilter ? <EntitiesFilters filters={filters} setFilters={setFilters} /> : undefined
      }
    >
      <EmptyListState
        title={uniqEntityStatusInfo.emptyListTitle}
        description={uniqEntityStatusInfo.emptyListDescription}
        isLoading={isFetching}
        isSearchUsed={isFiltersUsed}
        itemsCount={data?.pages?.[0]?.total ?? 0}
      />
      {(isLoading || (isFetching && !isFetchingNextPage)) && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {(!isFetching || isFetchingNextPage) && !!data?.pages?.[0]?.total && (
        <InfiniteScroll
          next={fetchNextPage}
          dataLength={data?.pages?.length ?? 0}
          hasMore={hasNextPage || false}
          style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          <div className="w-full flex flex-col gap-2 pb-2">
            {data?.pages?.map((entities, i) => (
              <Fragment key={i}>
                {entities?.data?.map((entity, index) => (
                  <ListItem
                    key={`${entity.id}_${index}`}
                    {...entity}
                    status={entity.status as EntityStatus}
                    handleOpenEntityInfoModal={handleOpenEntityInfoModal}
                  />
                ))}
              </Fragment>
            ))}
          </div>
        </InfiniteScroll>
      )}
      {isEntityInfoModalOpen && entityInfoModalProps && (
        <AdminEntityInfoModal
          isOpened={isEntityInfoModalOpen}
          handleCloseModal={setIsEntityInfoModalOpen}
          {...entityInfoModalProps}
        />
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(AdminEntities, Role.SR_ADMIN);
